import { Alert, Button, Form, Input, type InputProps } from '@heroui/react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '/logo-short.png';
import Background from '/sign-up-background.jpg';
import { useAuth } from '../../hooks/auth-provider';
import { EaseInOutView } from './EaseInOutView';

const inputProps: InputProps = {
  variant: 'flat',
  color: 'default',
  className: 'mb-3',
};

type ViewState = 'requestPasswordReset' | 'passwordResetEmailSent' | 'resetPassword';

export default function ResetPasswordPage() {
  const [queryParams] = useSearchParams();
  const [emailAddress, setEmailAddress] = useState('');
  const [passwordResetToken, setPasswordResetToken] = useState('');
  const [view, setView] = useState<ViewState>('requestPasswordReset');

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryEmailAddress = queryParams.get('emailAddress');
    const queryPasswordResetToken = queryParams.get('passwordResetToken');

    if (queryEmailAddress) {
      setEmailAddress(queryEmailAddress);
    }

    if (queryEmailAddress && queryPasswordResetToken) {
      setPasswordResetToken(queryPasswordResetToken);
      setView('resetPassword');
    }
  }, [queryParams]);

  function navigateAfterLogIn() {
    navigate(location.state?.redirectTo || '/', { replace: true });
  }

  return (
    <div className='flex items-stretch relative w-full flex-1 h-screen bg-gradient-to-bl from-primary-200 via-secondary-200 to-primary-300 p-4 md:p-10'>
      <div className='flex flex-col flex-1 bg-white/100 rounded-3xl md:rounded-r-none backdrop-blur p-3 overflow-y-auto'>
        <img src={Logo} alt='MeetingMD' className='h-auto self-center w-56 object-contain my-10' />
        <div className='p-4 flex flex-col self-center w-full max-w-md gap-4 relative overflow-hidden'>
          <EaseInOutView active={view === 'requestPasswordReset'}>
            <RequestPasswordResetView
              emailAddress={emailAddress}
              next={emailAddress => {
                setEmailAddress(emailAddress);
                setView('passwordResetEmailSent');
              }}
            />
          </EaseInOutView>

          <EaseInOutView active={view === 'passwordResetEmailSent'}>
            <PasswordResetEmailSentView emailAddress={emailAddress} />
          </EaseInOutView>

          <EaseInOutView active={view === 'resetPassword'}>
            <ResetPasswordView
              emailAddress={emailAddress}
              passwordResetToken={passwordResetToken}
              navigateAfterLogIn={navigateAfterLogIn}
            />
          </EaseInOutView>
        </div>
      </div>
      <div
        className='flex-col flex-1 hidden md:flex rounded-r-3xl bg-cover bg-top relative'
        style={{ backgroundImage: `url(${Background})` }}
      ></div>
    </div>
  );
}

function RequestPasswordResetView({
  emailAddress: prefilledEmailAddress,
  next,
}: {
  emailAddress: string;
  next: (emailAddress: string) => void;
}) {
  const [emailAddress, setEmailAddress] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { requestPasswordReset } = useAuth();

  useEffect(() => {
    if (prefilledEmailAddress) {
      setEmailAddress(prefilledEmailAddress);
    }
  }, [prefilledEmailAddress]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await requestPasswordReset({ emailAddress });
      next(emailAddress);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to request password reset. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form onSubmit={onSubmit} className='flex flex-col gap-4' validationBehavior='native'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Reset Password</h1>
      <p className='text-center text-sm text-gray-600 mb-2'>
        Enter your email to request a password reset link.
      </p>
      <Input
        {...inputProps}
        type='email'
        label='Email Address'
        placeholder='Enter your email'
        value={emailAddress}
        onChange={e => setEmailAddress(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <Button type='submit' size='lg' color='primary' className='w-full' isLoading={isLoading}>
        Submit
      </Button>
    </Form>
  );
}

function PasswordResetEmailSentView({ emailAddress }: { emailAddress: string }) {
  return (
    <div className='flex flex-col gap-4'>
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Check Your Email</h1>
      <p className='text-sm text-gray-600 mb-2'>
        If an account with <strong>{emailAddress}</strong> exists, we have sent a password reset
        link to that email address. Please check your inbox and follow the instructions to reset
        your password.
      </p>
    </div>
  );
}

function ResetPasswordView({
  emailAddress,
  passwordResetToken,
  navigateAfterLogIn,
}: {
  emailAddress: string;
  passwordResetToken: string;
  navigateAfterLogIn: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordMismatchError, setShowPasswordMismatchError] = useState(false);
  const { resetPassword } = useAuth();

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setShowPasswordMismatchError(false);

    if (password !== confirmPassword) {
      setShowPasswordMismatchError(true);
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({ emailAddress, passwordResetToken, newPassword: password });
      navigateAfterLogIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form
      onSubmit={handleResetPassword}
      className='flex flex-col gap-4'
      validationBehavior='native'
    >
      <h1 className='text-xl font-bold self-center mb-3 text-center'>Reset Password</h1>
      <Input
        {...inputProps}
        type='password'
        label='Password'
        value={password}
        onChange={e => setPassword(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      <Input
        {...inputProps}
        type='password'
        label='Confirm Password'
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        isRequired
        disabled={isLoading}
      />
      {showPasswordMismatchError && password !== confirmPassword && (
        <Alert className='mb-3' color='danger' variant='flat' title='Passwords do not match' />
      )}
      {error && <Alert className='mb-3' color='danger' variant='flat' title={error} />}
      <div className='flex self-stretch gap-3'>
        <Button type='submit' size='lg' color='primary' isLoading={isLoading} className='flex-1'>
          Reset Password and Log In
        </Button>
      </div>
    </Form>
  );
}
